import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeName = "midnight" | "ocean" | "forest" | "sunset" | "arctic";

export interface Theme {
  name: ThemeName;
  label: string;
  description: string;
  preview: {
    bg: string;
    card: string;
    primary: string;
    accent: string;
  };
}

export const themes: Theme[] = [
  {
    name: "midnight",
    label: "Midnight",
    description: "Deep dark with purple accents",
    preview: {
      bg: "#09090b",
      card: "#18181b",
      primary: "#5a6df2",
      accent: "#14b8a6",
    },
  },
  {
    name: "ocean",
    label: "Tokyo Night",
    description: "Deep purple & cyan glow",
    preview: {
      bg: "#1a1b26",
      card: "#24283b",
      primary: "#7aa2f7",
      accent: "#73daca",
    },
  },
  {
    name: "forest",
    label: "Nord",
    description: "Arctic frost & aurora",
    preview: {
      bg: "#2e3440",
      card: "#3b4252",
      primary: "#88c0d0",
      accent: "#a3be8c",
    },
  },
  {
    name: "sunset",
    label: "Dracula",
    description: "Purple & pink vampire",
    preview: {
      bg: "#282a36",
      card: "#44475a",
      primary: "#bd93f9",
      accent: "#ff79c6",
    },
  },
  {
    name: "arctic",
    label: "Catppuccin",
    description: "Warm pastel light mode",
    preview: {
      bg: "#eff1f5",
      card: "#ffffff",
      primary: "#8839ef",
      accent: "#179299",
    },
  },
];

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "arctic",
      setTheme: (theme) => {
        // Apply theme to document
        document.documentElement.setAttribute("data-theme", theme);
        set({ theme });
      },
    }),
    {
      name: "tradecraft-theme",
      onRehydrateStorage: () => (state) => {
        // Apply persisted theme on page load
        if (state?.theme) {
          document.documentElement.setAttribute("data-theme", state.theme);
        }
      },
    }
  )
);

// Initialize theme on module load
const initTheme = () => {
  const stored = localStorage.getItem("tradecraft-theme");
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme) {
        document.documentElement.setAttribute("data-theme", state.theme);
      }
    } catch {
      document.documentElement.setAttribute("data-theme", "arctic");
    }
  } else {
    document.documentElement.setAttribute("data-theme", "arctic");
  }
};

initTheme();
